import _snakeCase from 'lodash/snakeCase';

function clone(source) {
    if (Array.isArray(source)) {
        return source.map(value => clone(value));
    }
    else if (source && typeof source === 'object') {
        return Object.keys(source).reduce((memo, key) => {
            memo[key] = clone(source[key]);
            return memo;
        }, {});
    }
    return source;
}

/* @this ctx */
function merge() {
    const ctx = this || {};
    const [target, source, ...rest] = [...arguments].map(arg => clone(arg));

    if (typeof source === 'undefined' || (typeof source === 'object' && !source)) {
        return rest.length ? merge.call(ctx, target, ...rest) : target;
    }
    if (typeof target === 'undefined' || (typeof target === 'object' && !target)) {
        return rest.length ? merge.call(ctx, source, ...rest) : source;
    }
    if (typeof target === typeof source) {
        if (!Array.isArray(source) && typeof source === 'object') {
            for (const key of Object.keys(source)) {
                target[key] = merge.call({
                    files: ctx.files,
                    keys: (ctx.keys || []).concat(key)
                }, target[key], source[key]);
            }
            return rest.length ? merge.call(ctx, target, ...rest) : target;
        }
        return rest.length ? merge.call(ctx, source, ...rest) : source;
    }
    throw new TypeError(`Type missmatch (${ (typeof source) } over ${ (typeof target) }) trying to MERGE '${ ctx.files.join('\' over \'') }' at key '${ (ctx.keys || []).join(' -> ') }'.`);
}

/* @this ctx */
function combine() {
    const ctx = this || {};
    const [target, source, ...rest] = [...arguments].map(arg => clone(arg));

    if (typeof source === 'undefined' || (typeof source === 'object' && !source)) {
        return rest.length ? combine.call(ctx, target, ...rest) : target;
    }
    if (typeof target === 'undefined' || (typeof target === 'object' && !target)) {
        return rest.length ? combine.call(ctx, source, ...rest) : source;
    }
    if (!Array.isArray(source) && typeof source === 'object' && !Array.isArray(target) && typeof target === 'object') {
        const targetKeys = Object.keys(target);
        const sourceKeys = Object.keys(source);

        for (const key of targetKeys.concat(sourceKeys.filter(key => !targetKeys.includes(key)))) {
            target[key] = combine.call({
                files: ctx.files,
                keys: (ctx.keys || []).concat(key)
            }, target[key], source[key]);
        }
        return rest.length ? combine.call(ctx, target, ...rest) : target;
    }
    throw new TypeError(`Ambiguous value trying to COMBINE '${ ctx.files.join('\' with \'') }' at key '${ (ctx.keys || []).join(' -> ') }'.`);
}

const isUndefined = value => typeof value === 'undefined';
const isObject = object => !isUndefined(object) && object != null && typeof object === 'object';

const isEmpty = object =>
    (isObject(object)
        && (object.constructor === Object || object.constructor === Array)
        && Object.keys(object).length === 0)
    || !object;

const clean = obj => {
    for (const key in obj) {
        if (key in obj && (typeof obj[key] === 'undefined' || obj[key] === '')) {
            delete obj[key];
        }
    }
    return obj;
};

/**
 * @function pickBy
 * @description Native implementation of loadash "pickBy"
 *
 * @param {Object} object
 *
 * @returns {Object} Sanitized object
 */
const pickBy = object => {
    const obj = {};

    for (const key in object) {
        if (object[key] !== null && object[key] !== false && object[key] !== undefined) {
            obj[key] = object[key];
        }
    }

    return obj;
};

const resolve = (obj, path) => {
    const properties = path.split('.');
    const resolvedValue = properties.reduce((prev, curr) => prev && prev[curr], obj);

    if (!resolvedValue) {
        return resolvedValue === 0 ? 0 : '';
    }
    return resolvedValue;
};

/**
 * Implementaion of lodash omit
 */
const omit = (obj, keysToOmit) => {
    const keysMap = keysToOmit.reduce((acc, key) => {
        acc[key] = 1;
        return acc;
    }, {});

    return Object.keys(obj).reduce((acc, key) => {
        if (!keysMap[key]) {
            acc[key] = obj[key];
        }
        return acc;
    }, {});
};

/**
 * Returns the object without the keys which returns true for the predicate function
 * @param {*} obj
 * @param {*} predicateFn
 */
const omitBy = (obj, predicateFn) => {
    if (!predicateFn) {
        return obj;
    }
    return Object.keys(obj).reduce((acc, key) => {
        if (!predicateFn(obj[key])) {
            acc[key] = obj[key];
        }
        return acc;
    }, {});
};

const omitUndefinedKeys = obj => omitBy(obj, val => typeof val === 'undefined');

/**
 * Returns the nested value of a key in a nested object
 * @param {Object} obj
 * @param {Array|string} keys
 * @param {*} defaultValue
 * @returns {*} value of the key or the default value
 */
const getNestedValue = (obj, keys, defaultValue) => {
    const keysArr = Array.isArray(keys) ? keys : keys.split('.');
    let nestedValue = { ...obj };

    for (const key of keysArr) {
        const isObjectNestedValue = isObject(nestedValue);

        nestedValue = isObjectNestedValue && typeof nestedValue[key] !== 'undefined'
            ? nestedValue[key] : defaultValue;
        if (!isObjectNestedValue) {
            break;
        }
    }

    return nestedValue;
};

const isNull = val => typeof val === 'object' && !val;

const convertKeysToSnakeCase = obj => {
    if (!obj) {
        return {};
    }
    const newObj = {};

    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            const newKey = _snakeCase(key);

            newObj[newKey] = obj[key];
        }
    }
    return newObj;
};

export {
    clone,
    merge,
    combine,
    isEmpty,
    isUndefined,
    isObject,
    clean,
    pickBy,
    resolve,
    omit,
    omitBy,
    omitUndefinedKeys,
    getNestedValue,
    isNull,
    convertKeysToSnakeCase
};
